import { useCallback, useEffect, useRef, useState } from "react";

export type AutosaveStatus = "saved" | "unsaved" | "saving" | "failed";

export interface DraftAutosave {
    status: AutosaveStatus;
    flush: () => Promise<void>;
}

export const useDraftAutosave = <T>(
    value: T,
    save: (value: T) => Promise<void>,
    delayMs: number,
): DraftAutosave => {
    const [status, setStatus] = useState<AutosaveStatus>("saved");
    const latest = useRef(value);
    const lastSaved = useRef(value);
    const saveRef = useRef(save);
    const inFlight = useRef<Promise<void> | null>(null);
    const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

    const saveLatest = useCallback(async (): Promise<void> => {
        while (inFlight.current) {
            await inFlight.current.catch((): void => undefined);
        }
        if (latest.current === lastSaved.current) {
            return;
        }
        const saving = latest.current;
        setStatus("saving");
        const attempt = saveRef.current(saving).then(
            () => {
                lastSaved.current = saving;
                setStatus(latest.current === saving ? "saved" : "unsaved");
            },
            (error: Error) => {
                setStatus("failed");
                throw error;
            },
        );
        inFlight.current = attempt;
        try {
            await attempt;
        } finally {
            inFlight.current = null;
        }
    }, []);

    useEffect(() => {
        saveRef.current = save;
    }, [save]);

    const clearTimer = useCallback(() => {
        if (timer.current) {
            clearTimeout(timer.current);
            timer.current = null;
        }
    }, []);

    useEffect(() => {
        if (value === latest.current) {
            return;
        }
        latest.current = value;
        setStatus("unsaved");
        clearTimer();
        timer.current = setTimeout(() => {
            timer.current = null;
            saveLatest().catch((): void => undefined);
        }, delayMs);
    }, [value, delayMs, clearTimer, saveLatest]);

    useEffect(() => clearTimer, [clearTimer]);

    const flush = useCallback((): Promise<void> => {
        clearTimer();
        return saveLatest();
    }, [clearTimer, saveLatest]);

    return { status, flush };
};
